import { store } from './lib/store';
import { loginSuccess, User } from './features/user/userSlice';
import { fetchBalanceSuccess } from './features/tokens/tokensSlice';
import { USER } from './api/apiConfig';
import { getCountries } from '@/api/apiService';
import jsPDF from 'jspdf';
import { Marker, fetchUserGuidesSuccess } from  "@/features/guides/guidesSlice";

export const setUserAction = (user: User) => {
  const { last_guides, saved_guides } = user;
  store.dispatch(loginSuccess(user));
  if (user.tokens !== undefined) {
    store.dispatch(fetchBalanceSuccess(+user.tokens));
  }
  store.dispatch(fetchUserGuidesSuccess({ last_guides, saved_guides }));
  updateUserInLocalStorage(user);
};

export const updateUserInLocalStorage = updatedUser => {
  try {
    const existingUser = localStorage.getItem(USER);
    if (existingUser) {
      const userData = JSON.parse(existingUser);
      const mergedUser = { ...userData, ...updatedUser };
      localStorage.setItem(USER, JSON.stringify(mergedUser));
    }
  } catch (error) {
    console.error('Error updating user in localStorage:', error);
  }
};

export const updateTokensInLocalStorage = (tokens: number) => {
  try {
    const existingUser = localStorage.getItem(USER);
    if (existingUser) {
      const userData = JSON.parse(existingUser);
      userData.tokens = tokens;
      localStorage.setItem(USER, JSON.stringify(userData));
    }
  } catch (error) {
    console.error('Error updating tokens in localStorage:', error);
  }
};

// Map common country names to ISO codes for flag URLs
const countryToISOMap: Record<string, string> = {
  'united states': 'us',
  'united kingdom': 'gb',
  'france': 'fr',
  'germany': 'de',
  'spain': 'es',
  'italy': 'it',
  'japan': 'jp',
  'china': 'cn',
  'india': 'in',
  'canada': 'ca',
  'australia': 'au',
  'brazil': 'br',
  'mexico': 'mx',
  'russia': 'ru',
  'south korea': 'kr',
  'netherlands': 'nl',
  'switzerland': 'ch',
  'sweden': 'se',
  'norway': 'no',
  'denmark': 'dk',
  'poland': 'pl',
  'turkey': 'tr',
  'thailand': 'th',
  'singapore': 'sg',
  'malaysia': 'my',
  'indonesia': 'id',
  'philippines': 'ph',
  'vietnam': 'vn',
  'argentina': 'ar',
  'chile': 'cl',
  'colombia': 'co',
  'peru': 'pe',
  'egypt': 'eg',
  'south africa': 'za',
  'nigeria': 'ng',
  'kenya': 'ke',
  'morocco': 'ma',
  'israel': 'il',
  'saudi arabia': 'sa',
  'united arab emirates': 'ae',
  'qatar': 'qa',
  'kuwait': 'kw',
  'iran': 'ir',
  'iraq': 'iq',
  'pakistan': 'pk',
  'bangladesh': 'bd',
  'sri lanka': 'lk',
  'nepal': 'np',
  'myanmar': 'mm',
  'cambodia': 'kh',
  'laos': 'la',
  'mongolia': 'mn',
  'kazakhstan': 'kz',
  'uzbekistan': 'uz',
  'ukraine': 'ua',
  'belarus': 'by',
  'finland': 'fi',
  'estonia': 'ee',
  'latvia': 'lv',
  'lithuania': 'lt',
  'portugal': 'pt',
  'greece': 'gr',
  'bulgaria': 'bg',
  'romania': 'ro',
  'hungary': 'hu',
  'czech republic': 'cz',
  'slovakia': 'sk',
  'slovenia': 'si',
  'croatia': 'hr',
  'bosnia and herzegovina': 'ba',
  'serbia': 'rs',
  'montenegro': 'me',
  'albania': 'al',
  'north macedonia': 'mk',
  'moldova': 'md',
  'georgia': 'ge',
  'armenia': 'am',
  'azerbaijan': 'az',
  'austria': 'at',
  'belgium': 'be',
  'luxembourg': 'lu',
  'ireland': 'ie',
  'iceland': 'is',
  'cyprus': 'cy',
  'malta': 'mt',
  'andorra': 'ad',
  'monaco': 'mc',
  'san marino': 'sm',
  'liechtenstein': 'li',
  'vatican city': 'va'
};

export const flagUrl = (country) => {
  const countryKey = country?.toLowerCase();
  const isoCode = countryToISOMap[countryKey];
  
  if (isoCode) {
    return `https://flagcdn.com/w40/${isoCode}.png`;
  }

  // Fallback - try to use the country name as ISO code if it's 2 characters
  if (country?.length === 2) {
    return `https://flagcdn.com/w40/${country.toLowerCase()}.png`;
  }

  // Return a placeholder or null
  return null;
}

export const getGoogleMapsUrl = (markers: Marker[], place: string) => {
  if (markers?.length === 0) return "";

  const baseUrl = "https://www.google.com/maps/dir/?api=1&travelmode=walking&dir_action=navigate";
  const origin = `${markers[0].name},${place}`;
  const destination = `${markers[markers.length - 1].name},${place}`;
  const waypoints = markers.slice(1, -1)
      .map(marker => `${marker.name},${place}`)
      .join('|');

  return waypoints
      ? `${baseUrl}&origin=${origin}&destination=${destination}&waypoints=${waypoints}`
      : `${baseUrl}&origin=${origin}&destination=${destination}`;
};

export const getPlaceMapLink = (placeName: string) => {
  const baseURL = 'https://www.google.com/maps/search/?api=1&query=';
  const encodedPlaceName = encodeURIComponent(placeName);
  return `${baseURL}${encodedPlaceName}`;
}

export const getGoogleStaticMapUrl = async (markers: Marker[], place: string): Promise<string> => {
  if (markers?.length === 0) return "";

  const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";
  const size = "600x400";
  const maptype = "roadmap";

  // Add all markers as waypoints
  const markersParam = markers.map((marker, index) => {
    const label = String.fromCharCode(65 + index); // A, B, C, etc.
    return `markers=color:red%7Clabel:${label}%7C${encodeURIComponent(`${marker.name},${place}`)}`;
  }).join('&');

  // Create path for the route
  const pathPoints = markers.map(marker =>
    encodeURIComponent(`${marker.name},${place}`)
  ).join('|');
  const pathParam = `path=color:0x0000ff%7Cweight:3%7C${pathPoints}`;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_STATIC_API_KEY;
  const mapUrl = `${baseUrl}?size=${size}&maptype=${maptype}&${markersParam}&${pathParam}&key=${apiKey}`;

  // Create cache key from route data (excluding API key)
  const cacheKey = `static_map_${btoa(`${place}_${markers.map(m => m.name).join('_')}`)}`;

  // Check if cached version exists
  try {
    const cachedImage = localStorage.getItem(cacheKey);
    if (cachedImage) {
      return cachedImage; // Return base64 data URL
    }
  } catch (error) {
    console.warn('Error reading from localStorage:', error);
  }

  // Fetch and cache the image
  try {
    const response = await fetch(mapUrl);
    if (response.ok) {
      const blob = await response.blob();
      const reader = new FileReader();

      return new Promise((resolve) => {
        reader.onload = () => {
          const dataUrl = reader.result as string;

          // Cache the image (with size limit check)
          try {
            if (dataUrl.length < 1024 * 1024) { // 1MB limit
              localStorage.setItem(cacheKey, dataUrl);
            }
          } catch (error) {
            console.warn('Error caching image:', error);
          }

          resolve(dataUrl);
        };
        reader.readAsDataURL(blob);
      });
    }
  } catch (error) {
    console.warn('Error fetching static map:', error);
  }

  // Fallback to original URL if fetch fails
  return mapUrl;
}

export const generatePDF = async (markers: Marker[], place: string) => {
  const doc = new jsPDF();
  const title = 'Your Itinerary';

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const textWidth = pageWidth - margin * 2;

  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(title , margin, 22);
  doc.setFont(undefined, 'normal');

  doc.setFontSize(12);
  doc.setTextColor(0, 0, 255);
  const mapLinkWidth = doc.getStringUnitWidth('Show on Google Map') * 12 / doc.internal.scaleFactor;
  const mapLinkX = pageWidth - margin - mapLinkWidth;
  doc.textWithLink('Show on Google Map', mapLinkX, 22, { url: getGoogleMapsUrl(markers, place) });
  doc.setTextColor(0, 0, 0);

  let y = 45;
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 20;

  markers.forEach((marker, index) => {
    // Check if we need a new page (smaller buffer to use more space)
    if (y > pageHeight - 30) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(`${index + 1}. ${marker.name || 'Unnamed Marker'}`, margin, y);
    y += 8;

    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');

    if (marker.description) {
      const lines = doc.splitTextToSize(`${marker.description}`, textWidth);
      doc.text(lines, margin, y);
      y += lines.length * 5 + 4;
    }

    if (marker.long_description) {
      const lines = doc.splitTextToSize(`${marker.long_description}`, textWidth);
      doc.text(lines, margin, y);
      y += lines.length * 5 + 4;
    }

    if (marker.name) {
      doc.textWithLink('Show on maps', margin, y, { url: getPlaceMapLink(`${marker.name},${place}`) });
      doc.setTextColor(0, 0, 0);
      y += 6;
    }

    y += 8; // Space between markers
  });

  // Add Google Maps static image on last page
  doc.addPage();

  try {
    // Add title for the map page
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Route Map', margin, 30);

    // Generate Google Static Maps URL
    const staticMapUrl = await getGoogleStaticMapUrl(markers, place);

    // Add the static map image
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = 150;
    doc.addImage(staticMapUrl, 'PNG', margin, 45, imgWidth, imgHeight);

    // Add clickable link below the image
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 255);
    doc.textWithLink('Open in Google Maps', margin, 210, { url: getGoogleMapsUrl(markers, place) });
    doc.setTextColor(0, 0, 0);
  } catch (error) {
    console.warn('Could not add map image to PDF:', error);
    // Just add the link if image fails
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Route Map', margin, 30);
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 255);
    doc.textWithLink('Open in Google Maps', margin, 50, { url: getGoogleMapsUrl(markers, place) });
    doc.setTextColor(0, 0, 0);
  }

  const safePlace = place.replace(/[^a-z0-9_-]/gi, '_');
  const filename = safePlace + '.pdf';

  doc.save(filename);
};
